
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../course/components/Header';
import Footer from '../course/components/Footer';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Button from '../../components/base/Button';

interface ConsultingPackage {
  id: string;
  name: string;
  price: string;
  duration: string;
  description: string;
  features: string[];
  isPopular?: boolean;
}

function ConsultingPage() {
  const navigate = useNavigate();

  const handleLoginClick = () => {
    window.open('http://michael-jackson-s-school7.teachable.com/sign_in', '_blank');
  };

  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const [bookingStatus, setBookingStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [bookingMessage, setBookingMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    packageType: '',
    preferredDate: '',
    preferredTime: '',
    businessGoals: '',
    currentStage: '',
    specificQuestions: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const consultingPackages: ConsultingPackage[] = [
    {
      id: 'quick-call',
      name: 'Quick Consultation',
      price: '$97',
      duration: '30 minutes',
      description: 'Perfect for specific questions or getting unstuck on a particular step',
      features: [
        '30-minute phone or video call',
        'Specific question answers',
        'Personalized guidance',
        'Follow-up email summary',
        'Same-day scheduling available'
      ]
    },
    {
      id: 'deep-dive',
      name: 'Deep Dive Session',
      price: '$197',
      duration: '60 minutes',
      description: 'Comprehensive review of your entire licensing process and strategy',
      features: [
        '60-minute intensive session',
        'Complete application review',
        'Location assessment',
        'Business structure guidance',
        'Custom action plan',
        'Priority email support for 30 days'
      ],
      isPopular: true
    },
    {
      id: 'done-with-you',
      name: 'Done-With-You Package',
      price: '$497',
      duration: '3 sessions',
      description: 'Complete hand-holding through your entire licensing journey',
      features: [
        '3 one-hour sessions over 90 days',
        'Application preparation assistance',
        'Document review and feedback',
        'Location scouting guidance',
        'Bond and insurance shopping help',
        'Unlimited email support',
        'Priority scheduling'
      ]
    }
  ];

  const timeSlots = [
    { value: '9:00 AM', label: '9:00 AM' },
    { value: '10:00 AM', label: '10:00 AM' },
    { value: '11:00 AM', label: '11:00 AM' },
    { value: '1:00 PM', label: '1:00 PM' },
    { value: '2:00 PM', label: '2:00 PM' },
    { value: '3:00 PM', label: '3:00 PM' },
    { value: '4:00 PM', label: '4:00 PM' },
    { value: '5:00 PM', label: '5:00 PM' }
  ];

  const currentStageOptions = [
    { value: 'just-starting', label: 'Just getting started' },
    { value: 'researching', label: 'Researching requirements' },
    { value: 'preparing-application', label: 'Preparing application' },
    { value: 'submitted-waiting', label: 'Submitted and waiting' },
    { value: 'need-location', label: 'Need help with location' },
    { value: 'other', label: 'Other' }
  ];

  const handleBookConsultation = (packageId: string) => {
    const selectedPkg = consultingPackages.find(pkg => pkg.id === packageId);
    
    // Direct redirect to Teachable links
    const teachableLinks = {
      'quick-call': 'https://michael-jackson-s-school7.teachable.com/l/coaching/991641/quick-start',
      'deep-dive': 'https://michael-jackson-s-school7.teachable.com/l/coaching/991674/deep-dive-session',
      'done-with-you': 'https://michael-jackson-s-school7.teachable.com/l/coaching/991675/donewithyou-package'
    };
    
    const teachableUrl = teachableLinks[packageId as keyof typeof teachableLinks];
    if (teachableUrl) {
      window.open(teachableUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Fallback to modal for other packages
    setSelectedPackage(packageId);
    setFormData(prev => ({
      ...prev,
      packageType: selectedPkg ? `${selectedPkg.name} (${selectedPkg.price})` : packageId
    }));
    setShowBookingModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3g1tq1rrun81ijtp0u0', {
        method: 'POST',
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          packageType: '',
          preferredDate: '',
          preferredTime: '',
          businessGoals: '',
          currentStage: '',
          specificQuestions: ''
        });
        setTimeout(() => {
          setShowBookingModal(false);
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowBookingModal(false);
    setSubmitStatus('idle');
  };

  const openCalendly = (packageName: string) => {
    // Direct redirect to Teachable links based on package name
    const teachableLinks = {
      'Quick Start Package': 'https://michael-jackson-s-school7.teachable.com/l/coaching/991641/quick-start',
      'Complete Guide Package': 'https://michael-jackson-s-school7.teachable.com/l/coaching/991674/deep-dive-session',
      'Premium Support Package': 'https://michael-jackson-s-school7.teachable.com/l/coaching/991675/donewithyou-package'
    };
    
    const teachableUrl = teachableLinks[packageName as keyof typeof teachableLinks];
    if (teachableUrl) {
      window.open(teachableUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Fallback to Calendly modal
    setSelectedPackage(packageName);
    setShowCalendly(true);
  };

  const closeCalendly = () => {
    setShowCalendly(false);
    setSelectedPackage('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Expert Consulting Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Get personalized guidance from someone who has successfully helped hundreds of entrepreneurs obtain their Georgia dealer license
            </p>
            
            {/* Credentials */}
            <div className="bg-white rounded-2xl p-6 shadow-lg max-w-4xl mx-auto">
              <div className="flex items-center justify-center mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/e5cf8b60b8d9525b133b9c4eb49f9999.jpg" 
                  alt="Consultant" 
                  className="w-20 h-20 rounded-full object-cover mr-4"
                />
                <div className="text-left">
                  <h3 className="text-xl font-bold text-gray-900">Georgia Dealer License Expert</h3>
                  <p className="text-gray-600">Professional Licensing Consultant</p>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                    <span>500+ Successful Licenses</span>
                    <span>•</span>
                    <span>10+ Years Experience</span>
                    <span>•</span>
                    <span>98% Success Rate</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-center">
                "I've personally guided hundreds of entrepreneurs through the Georgia dealer licensing process. 
                My proven system and personalized approach ensures you avoid costly mistakes and get licensed fast."
              </p>
            </div>
          </div>

          {/* Consulting Packages */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {consultingPackages.map((pkg) => (
              <div 
                key={pkg.id} 
                className={`bg-white rounded-2xl p-8 shadow-lg relative ${
                  pkg.isPopular ? 'ring-2 ring-yellow-400 transform scale-105' : ''
                }`}
              >
                {pkg.isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-black px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <div className="text-3xl font-bold text-yellow-600 mb-1">{pkg.price}</div>
                  <div className="text-gray-500 text-sm">{pkg.duration}</div>
                </div>
                
                <p className="text-gray-600 text-center mb-6">{pkg.description}</p>
                
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <i className="ri-check-line text-green-500 flex-shrink-0"></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => handleBookConsultation(pkg.id)}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    pkg.isPopular
                      ? 'bg-yellow-500 text-black hover:bg-yellow-400'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  Book Now
                </button>
              </div>
            ))}
          </div>

          {/* Why Choose Consulting */}
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Why Choose Personal Consulting?
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-star-line text-2xl text-yellow-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Personalized Guidance</h4>
                <p className="text-gray-600 text-sm">Get advice tailored to your specific situation and goals</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-2xl text-yellow-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Save Time</h4>
                <p className="text-gray-600 text-sm">Avoid months of research and trial-and-error</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-yellow-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Avoid Mistakes</h4>
                <p className="text-gray-600 text-sm">Prevent costly errors that could delay your license</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-rocket-line text-2xl text-yellow-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Fast Track</h4>
                <p className="text-gray-600 text-sm">Get licensed in the shortest time possible</p>
              </div>
            </div>
          </div>

          {/* Success Stories */}
          <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Success Stories
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20African%20American%20businessman%20entrepreneur%20confident%20smile%20headshot%20suit%20tie%20successful%20male%20dealer&width=50&height=50&seq=marcus-johnson&orientation=squarish" 
                    alt="Marcus Johnson" 
                    className="w-12 h-12 rounded-full object-cover mr-3"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">Marcus Johnson</div>
                    <div className="text-sm text-gray-600">Atlanta, GA</div>
                  </div>
                </div>
                <p className="text-gray-700 text-sm italic">
                  "The consulting call saved me months of confusion. Got my license approved in just 8 weeks!"
                </p>
              </div>
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20businesswoman%20female%20entrepreneur%20confident%20smile%20headshot%20suit%20successful%20woman%20dealer%20automotive&width=50&height=50&seq=sarah-williams&orientation=squarish" 
                    alt="Sarah Williams" 
                    className="w-12 h-12 rounded-full object-cover mr-3"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">Sarah Williams</div>
                    <div className="text-sm text-gray-600">Savannah, GA</div>
                  </div>
                </div>
                <p className="text-gray-700 text-sm italic">
                  "The done-with-you package was worth every penny. Hand-holding through the entire process."
                </p>
              </div>
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20Hispanic%20Latino%20businessman%20entrepreneur%20confident%20headshot%20suit%20successful%20male%20dealer%20automotive%20business&width=50&height=50&seq=david-rodriguez&orientation=squarish" 
                    alt="David Rodriguez" 
                    className="w-12 h-12 rounded-full object-cover mr-3"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">David Rodriguez</div>
                    <div className="text-sm text-gray-600">Columbus, GA</div>
                  </div>
                </div>
                <p className="text-gray-700 text-sm italic">
                  "Avoided a major location mistake that would have cost me thousands. Expert advice is priceless."
                </p>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Consulting FAQ
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">How quickly can I schedule?</h4>
                <p className="text-gray-600 text-sm mb-4">Most consultations can be scheduled within 24-48 hours. Same-day appointments available for urgent needs.</p>
                
                <h4 className="font-semibold text-gray-900 mb-2">What if I need to reschedule?</h4>
                <p className="text-gray-600 text-sm mb-4">No problem! Just give us 24 hours notice and we'll reschedule at no charge.</p>
                
                <h4 className="font-semibold text-gray-900 mb-2">Do you offer refunds?</h4>
                <p className="text-gray-600 text-sm">If you're not satisfied with your consultation, we'll refund your money within 7 days.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">What should I prepare?</h4>
                <p className="text-gray-600 text-sm mb-4">Bring any documents you have, your questions list, and details about your business goals and timeline.</p>
                
                <h4 className="font-semibold text-gray-900 mb-2">Can you help with other states?</h4>
                <p className="text-gray-600 text-sm mb-4">Our expertise is specifically in Georgia licensing. We focus on what we know best to ensure your success.</p>
                
                <h4 className="font-semibold text-gray-900 mb-2">Is follow-up support included?</h4>
                <p className="text-gray-600 text-sm">Yes! All packages include follow-up support via email for the specified duration.</p>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-black mb-4">
              Ready to Get Started?
            </h3>
            <p className="text-black/80 text-lg mb-6">
              Don't navigate the licensing process alone. Get expert guidance and avoid costly mistakes.
            </p>
            <div className="flex items-center justify-center space-x-4">
              <a
                href="mailto:Info@gerogiadealerlicense.com?subject=Consultation Inquiry"
                className="bg-black text-white px-8 py-3 rounded-full font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line mr-2"></i>
                Email Us
              </a>
              <button
                onClick={() => handleBookConsultation('deep-dive')}
                className="border-2 border-black text-black px-8 py-3 rounded-full font-semibold hover:bg-black hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-line mr-2"></i>
                Book Now
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Consultation Packages */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Choose Your Consultation Package
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get personalized guidance tailored to your specific needs and timeline
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Quick Start Package */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-yellow-400 transition-all">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-rocket-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Quick Start</h3>
                <div className="text-3xl font-bold text-gray-900 mb-2">$97</div>
                <p className="text-gray-600">30-minute focused session</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">License requirements overview</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Application process walkthrough</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Timeline and next steps</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Q&A session</span>
                </li>
              </ul>

              <button
                onClick={() => openCalendly('Quick Start Package')}
                className="w-full bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Book Quick Start
              </button>
            </div>

            {/* Complete Guide Package */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-2xl p-8 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-yellow-400 text-black px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </span>
              </div>
              
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-guide-line text-2xl text-yellow-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Complete Guide</h3>
                <div className="text-3xl font-bold text-gray-900 mb-2">$197</div>
                <p className="text-gray-600">60-minute comprehensive session</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Everything in Quick Start</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Business structure planning</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Location and zoning guidance</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Insurance requirements</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Financing options review</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Custom action plan</span>
                </li>
              </ul>

              <button
                onClick={() => openCalendly('Complete Guide Package')}
                className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-semibold py-3 px-6 rounded-lg hover:from-yellow-500 hover:to-yellow-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Book Complete Guide
              </button>
            </div>

            {/* Premium Support Package */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-purple-400 transition-all">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-vip-crown-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Premium Support</h3>
                <div className="text-3xl font-bold text-gray-900 mb-2">$497</div>
                <p className="text-gray-600">90-minute deep dive + follow-up</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Everything in Complete Guide</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Market analysis for your area</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Inventory sourcing strategies</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Marketing and sales planning</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">30-day follow-up call</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">Email support for 30 days</span>
                </li>
              </ul>

              <button
                onClick={() => openCalendly('Premium Support Package')}
                className="w-full bg-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Book Premium Support
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Schedule Your Consultation
                </h3>
                {selectedPackage && (
                  <p className="text-sm text-gray-600 mt-1">
                    Selected: {selectedPackage}
                  </p>
                )}
              </div>
              <button
                onClick={closeCalendly}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>
            <div className="h-[600px]">
              <iframe
                src="https://calendly.com/180trustministries/30min"
                width="100%"
                height="100%"
                frameBorder="0"
                title="Schedule Consultation"
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-900">Book Your Consultation</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <p className="text-gray-600 mt-2">
                Selected: <span className="font-semibold">{formData.packageType}</span>
              </p>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form className="p-6 space-y-6">
              {submitStatus === 'success' && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-6 py-4 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-xl mr-2"></i>
                    <span className="font-medium">Consultation booked successfully! We'll contact you within 24 hours to confirm details.</span>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-xl mr-2"></i>
                    <span className="font-medium">Booking failed. Please try again or contact us directly.</span>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  name="name"
                  placeholder="Enter your full name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                />
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="your@email.com"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>

              <Input
                label="Phone Number"
                name="phone"
                type="tel"
                placeholder="(555) 123-4567"
                required
                value={formData.phone}
                onChange={handleInputChange}
              />

              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Preferred Date"
                  name="preferredDate"
                  type="date"
                  required
                  value={formData.preferredDate}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                />
                <Select
                  label="Preferred Time"
                  name="preferredTime"
                  options={timeSlots}
                  placeholder="Select time"
                  required
                  value={formData.preferredTime}
                  onChange={(value) => handleSelectChange('preferredTime', value)}
                />
              </div>

              <Select
                label="Current Stage"
                name="currentStage"
                options={currentStageOptions}
                placeholder="Where are you in the licensing process?"
                required
                value={formData.currentStage}
                onChange={(value) => handleSelectChange('currentStage', value)}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Goals
                </label>
                <textarea
                  name="businessGoals"
                  placeholder="Tell us about your business goals and timeline..."
                  value={formData.businessGoals}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                  rows={3}
                  maxLength={500}
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.businessGoals.length}/500 characters
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specific Questions (Optional)
                </label>
                <textarea
                  name="specificQuestions"
                  placeholder="Any specific questions or concerns you'd like to discuss?"
                  value={formData.specificQuestions}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                  rows={3}
                  maxLength={500}
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.specificQuestions.length}/500 characters
                </div>
              </div>

              <div className="flex items-center space-x-4 pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-semibold py-3 px-6 rounded-lg hover:from-yellow-500 hover:to-yellow-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Booking...
                    </>
                  ) : (
                    <>
                      <i className="ri-calendar-check-line mr-2"></i>
                      Book Consultation
                    </>
                  )}
                </Button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default ConsultingPage;
